// VesselManagement page: allows Shipowner to manage vessels (create, edit, delete).
import React, { useState, useEffect, useContext } from 'react';
import { 
  Container, Typography, TextField, Button, Table, TableHead, 
  TableRow, TableCell, TableBody, Box, Card, CardContent,
  Grid, TableContainer, Paper, IconButton, 
  CircularProgress, Alert, Snackbar
} from '@mui/material';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import apiClient from '../api/apiClient';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

export default function VesselManagement() {
  const { user } = useContext(AuthContext);
  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [imo, setImo] = useState('');
  const [name, setName] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const fetchVessels = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get('/vessels'); 
      setVessels(response.data.data || []);
    } catch (err) {
      console.error("Failed to fetch vessels:", err);
      if (err.response && err.response.data) {
        const p = err.response.data;
        setError(p.detail || p.title || 'Failed to load vessels.');
      } else {
        setError('Failed to load vessels.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'SHIPOWNER') {
        fetchVessels();
    }
  }, [user]);

  const clearForm = () => {
    setImo('');
    setName('');
    setIsEditing(false);
    setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    const apiData = { name };
    if (!isEditing) {
        apiData.imo = imo;
    }
    
    const url = isEditing ? `/vessels/${imo}` : '/vessels';
    const method = isEditing ? 'put' : 'post';
    const successMessage = isEditing ? 'Vessel updated successfully!' : 'Vessel created successfully!';
    const failureMessage = isEditing ? 'Failed to update vessel.' : 'Failed to create vessel.';

    try {
      await apiClient[method](url, apiData);
      setSnackbarMessage(successMessage);
      setSnackbarOpen(true);
      clearForm();
      fetchVessels();
    } catch (err) {
      console.error(`${failureMessage}:`, err);
      if (err.response && err.response.data) {
        const p = err.response.data;
        setFormError(p.detail || p.title || failureMessage);
      } else {
        setFormError(failureMessage);
      }
    }
  };

  const handleEdit = (vessel) => {
    setImo(vessel.imo);
    setName(vessel.name);
    setIsEditing(true);
    setFormError('');
  };

  const handleDelete = async (vesselImo, vesselName) => {
    if (window.confirm(`Are you sure you want to delete vessel ${vesselName} (IMO: ${vesselImo})?`)) {
        setError('');
        try {
            await apiClient.delete(`/vessels/${vesselImo}`);
            setSnackbarMessage('Vessel deleted successfully!');
            setSnackbarOpen(true);
            fetchVessels();
            if (isEditing && imo === vesselImo) {
                clearForm();
            }
        } catch (err) {
            console.error("Failed to delete vessel:", err);
            if (err.response && err.response.data) {
              const p = err.response.data;
              setError(p.detail || p.title || 'Failed to delete vessel.');
            } else {
              setError('Failed to delete vessel.');
            }
        }
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  return (
    <div className="page-container">
      <Container>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1565c0' }}>
            Vessel Management
          </Typography>
        </Box>
        
        <Card className="card form-container" sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, mb: 2, display: 'flex', alignItems: 'center' }}>
              <DirectionsBoatIcon sx={{ mr: 1, color: '#1565c0' }} />
              {isEditing ? 'Edit Vessel' : 'Add New Vessel'}
            </Typography>
            {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    label="IMO Number"
                    value={imo}
                    onChange={(e) => setImo(e.target.value)}
                    fullWidth
                    required
                    variant="outlined"
                    placeholder="Enter IMO number"
                    disabled={isEditing}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Vessel Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    required
                    variant="outlined"
                    placeholder="Enter vessel name"
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                      type="submit" 
                      variant="contained" 
                      startIcon={isEditing ? <SaveIcon /> : <AddIcon />}
                      className="action-button"
                      sx={{ 
                        bgcolor: '#1565c0',
                        '&:hover': { bgcolor: '#0d47a1' },
                        height: '56px',
                        flexGrow: 1
                      }}
                    >
                      {isEditing ? 'Update Vessel' : 'Add Vessel'}
                    </Button>
                    
                    {isEditing && (
                      <Button 
                        type="button" 
                        variant="outlined"
                        onClick={clearForm}
                        sx={{ 
                          height: '56px',
                          borderColor: '#999',
                          color: '#666'
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
        
        <Box sx={{ mt: 4, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
            Your Vessels
          </Typography>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TableContainer component={Paper} className="table-container">
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>IMO Number</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Vessel Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                    </TableCell>
                </TableRow>
              ) : vessels.length > 0 ? (
                vessels.map((v) => (
                  <TableRow 
                    key={v.imo}
                    sx={{ 
                      '&:hover': { backgroundColor: '#f9f9f9' },
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {v.imo}
                      </Typography>
                    </TableCell>
                    <TableCell>{v.name}</TableCell>
                    <TableCell>
                      <IconButton 
                        component={Link}
                        to={`/bunkerings?vessel=${v.imo}`}
                        size="small"
                        sx={{ color: '#1565c0', mr: 1 }}
                        title="View Bunkerings"
                      >
                        <LocalShippingIcon />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleEdit(v)}
                        size="small"
                        sx={{ color: '#1565c0' }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        color="error" 
                        size="small"
                        onClick={() => handleDelete(v.imo, v.name)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">No vessels found. Add one above.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
      
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={4000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
          <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
              {snackbarMessage}
          </Alert>
      </Snackbar>
    </div>
  );
}
